import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/layout/Layout';
import { balanceReceiptsAPI } from '../lib/api';
import { receiptUrl, RECEIPT_ACCEPT } from '../components/admin/BankTransferReceiptFields';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Receipt, UploadCloud, Loader2, CheckCircle, XCircle, Clock, Eye, Paperclip, X } from 'lucide-react';

const STATUS_STYLE = {
    pending: { label: 'Pendiente de revisión', cls: 'bg-amber-500/15 text-amber-300', Icon: Clock },
    approved: { label: 'Aprobado · saldo agregado', cls: 'bg-emerald-500/15 text-emerald-300', Icon: CheckCircle },
    rejected: { label: 'Rechazado', cls: 'bg-red-500/15 text-red-300', Icon: XCircle },
};
const MAX_BYTES = 12 * 1024 * 1024;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtAmount = (a, c) => `${c === 'USD' ? '$' : '€'}${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

const SubmitReceiptPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('EUR');
    const [note, setNote] = useState('');
    const [file, setFile] = useState(null);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await balanceReceiptsAPI.mine();
            setItems(data.requests || []);
        } catch {
            toast.error('Error al cargar tus comprobantes');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const pickFile = (f) => {
        if (!f) { setFile(null); return; }
        if (f.size > MAX_BYTES) { toast.error('Archivo demasiado grande (máx 12 MB)'); return; }
        setFile(f);
    };

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error('Indica el importe transferido'); return; }
        if (!file) { toast.error('Adjunta el comprobante o una imagen'); return; }
        setSending(true);
        try {
            const fd = new FormData();
            fd.append('amount', String(amt));
            fd.append('currency', currency);
            fd.append('note', note);
            fd.append('file', file);
            await balanceReceiptsAPI.submit(fd);
            toast.success('Comprobante enviado. Te avisaremos cuando sea revisado.');
            setAmount(''); setNote(''); setFile(null);
            if (fileRef.current) fileRef.current.value = '';
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar el comprobante');
        } finally {
            setSending(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6" data-testid="submit-receipt-page">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1973B8]/15 border border-[#1973B8]/30">
                        <Receipt className="w-6 h-6 text-[#7CB1E5]" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Enviar Comprobante</h1>
                        <p className="text-sm text-slate-400 font-light">Sube el justificante de tu transferencia o una imagen · Al aprobarse, el saldo se agrega a tu cuenta</p>
                    </div>
                </div>

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5 space-y-4" data-testid="submit-receipt-card">
                    <p className="text-white font-semibold text-sm flex items-center gap-2"><UploadCloud className="w-4 h-4 text-[#7CB1E5]" /> Nuevo comprobante</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label className="text-slate-400 text-xs">Importe transferido</Label>
                            <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                                className="bg-slate-950 border-slate-800 text-white text-sm" data-testid="receipt-amount-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-400 text-xs">Moneda</Label>
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                                className="w-full h-10 rounded-md bg-slate-950 border border-slate-800 text-white text-sm px-3" data-testid="receipt-currency-select">
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-slate-400 text-xs">Comprobante o imagen (JPG, PNG, WEBP, GIF, PDF · máx 12 MB)</Label>
                        <input ref={fileRef} type="file" accept={RECEIPT_ACCEPT} className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="receipt-file-input" />
                        {file ? (
                            <div className="flex items-center gap-2 rounded-md bg-slate-950 border border-emerald-500/40 px-3 py-2 text-xs" data-testid="receipt-file-selected">
                                <Paperclip className="w-3.5 h-3.5 text-emerald-300" />
                                <span className="text-white truncate flex-1">{file.name}</span>
                                <span className="text-slate-500">{(file.size / 1024).toFixed(0)} KB</span>
                                <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="text-slate-400 hover:text-white" data-testid="receipt-file-clear"><X className="w-3.5 h-3.5" /></button>
                            </div>
                        ) : (
                            <button type="button" onClick={() => fileRef.current?.click()}
                                className="w-full rounded-md border border-dashed border-slate-700 bg-slate-950 px-3 py-4 text-xs text-slate-400 hover:border-[#1973B8] hover:text-white transition-colors"
                                data-testid="receipt-file-pick">
                                Haz clic para seleccionar el archivo
                            </button>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-slate-400 text-xs">Nota (opcional)</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Transferencia realizada el día 12"
                            className="bg-slate-950 border-slate-800 text-white text-sm" data-testid="receipt-note-input" />
                    </div>
                    <Button onClick={submit} disabled={sending || !file || !amount}
                        className="bg-[#1973B8] hover:bg-[#1973B8]/85 text-white" data-testid="receipt-submit-btn">
                        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                        Enviar comprobante
                    </Button>
                </div>

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-800">
                        <p className="text-white font-semibold text-sm">Mis comprobantes</p>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#7CB1E5] animate-spin" /></div>
                    ) : items.length === 0 ? (
                        <p className="text-slate-600 text-sm text-center py-10" data-testid="receipt-empty">Aún no has enviado ningún comprobante.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">
                            {items.map((r) => {
                                const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                                return (
                                    <div key={r.id} className="px-4 py-3 flex items-start gap-3" data-testid={`receipt-item-${r.id}`}>
                                        <Receipt className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm font-medium">{fmtAmount(r.amount, r.currency)}</p>
                                            <p className="text-slate-500 text-[11px] truncate">{r.filename} · {fmtDate(r.created_at)}</p>
                                            {r.note && <p className="text-slate-400 text-xs mt-0.5">Nota: {r.note}</p>}
                                            {r.reject_reason && (
                                                <p className="text-red-300 text-xs mt-1 bg-red-500/10 border border-red-500/25 rounded-lg px-2.5 py-1.5" data-testid={`receipt-reject-reason-${r.id}`}>
                                                    Motivo: {r.reject_reason}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold ${st.cls}`} data-testid={`receipt-status-${r.id}`}>
                                                <st.Icon className="w-3 h-3" /> {st.label}
                                            </span>
                                            <a href={receiptUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" title="Ver comprobante" data-testid={`receipt-view-${r.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SubmitReceiptPage;
